import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  Sparkles,
  MessageSquarePlus,
  Loader2,
  BookOpen,
  FileText,
  Copy,
  Check,
} from "lucide-react";
import { motion } from "framer-motion";
import { Handle, Position, useStore, useReactFlow } from "reactflow";
import { useFlowContext } from "../hooks/useFlowContext";
import { useAuth } from "../contexts/AuthContext";
import useFeatureFlags from "../hooks/useFeatureFlags";
import ReactMarkdown from "react-markdown";
import HighlightHandler from "./HighlightHandler";
import LoginModal from "./LoginModal";
import ColorPicker from "./ColorPicker";
import "../styles/EnergyFlow.css";

// Custom hook for responsive expansion
const useResponsiveExpand = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isDesktop;
};

const ResponseNode = ({ data }) => {
  const {
    handleTextHighlight,
    updateNodeExpansion,
    handleCustomQuestion,
    handleSourceClick,
    handleReflectAndPlayAction,
    handleExploreAction,
    bringNodeToFront,
    updateNodeData,
    focusNode,
  } = useFlowContext();

  const { isAuthenticated, login } = useAuth();
  const featureFlags = useFeatureFlags();
  const reactFlowInstance = useReactFlow(); // Get live ReactFlow instance

  // Track zoom level for adaptive styling
  const zoom = useStore((state) => state.transform[2]);

  const [customQuestion, setCustomQuestion] = useState("");
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);
  const [loadingSourceId, setLoadingSourceId] = useState(null); // Track which source is loading
  const [isLearnMoreActive, setIsLearnMoreActive] = useState(false); // Track learn more highlighting
  const [loadingQuestionId, setLoadingQuestionId] = useState(null); // Track which suggested question is loading
  const [isContinueJourneyLoading, setIsContinueJourneyLoading] =
    useState(false); // Track Continue Journey loading
  const [showLoginModal, setShowLoginModal] = useState(false); // Track login modal visibility
  const [isCopied, setIsCopied] = useState(false); // Track copy state
  const contentRef = useRef(null);
  const inputRef = useRef(null);

  // Check if this is the first node
  const isFirstNode = data?.isFirstNode || false;

  // Check if this is the last (most recent) node
  const isLastNode = data?.isLastNode || false;

  // Debug: Log the data being received
  useEffect(() => {
    // console.log("ResponseNode data:", data);
    // console.log("Response content:", data?.response);
    // console.log("Question:", data?.question);
    // console.log("isReflectAndPray flag:", data?.isReflectAndPray);
    // console.log("isReflection flag:", data?.isReflection);
    // console.log("🔍 isExplore flag:", data?.isExplore);
    // console.log("🔍 isExplore value type:", typeof data?.isExplore);
    // console.log("🔍 isExplore truthiness:", !!data?.isExplore);
    // console.log("isContinueJourney flag:", data?.isContinueJourney);
    // console.log(
    //   "Should hide Reflect and Pray button:",
    //   !!data?.isReflectAndPray
    // );
    // console.log("Should show Continue Journey:", !!data?.isReflection);
    // console.log("Should show Explore emoji:", !!data?.isExplore);
    // Full data dump for Discover nodes
    // if (data?.question === "Discover" || data?.question?.includes("Discover")) {
    //   console.log("🎯 FULL DISCOVER NODE DATA:", JSON.stringify(data, null, 2));
    // }
  }, [data]);

  // Check if this is a source node for special styling
  const isSourceNode = data.isFromSource;
  const isSummary = data.isSummary;
  const isReflectAndPray = data.isReflectAndPray;
  const isReflection = data.isReflection || false; // New flag for reflection responses
  const isContinueJourney = data.isContinueJourney || false; // New flag for Continue Journey nodes
  const isExplore = data.isExplore; // New flag for Explore (I'm feeling blessed) responses

  // Smart responsive initial state
  const isDesktop = useResponsiveExpand();
  const [isExpanded, setIsExpanded] = useState(
    data.isExpanded ?? (isContinueJourney || isReflection || isDesktop)
  );

  // Shimmer priority system - only one shimmer at a time
  const getActiveShimmer = () => {
    if (loadingSourceId === "reflect-and-pray") return "reflect"; // Highest priority
    if (loadingSourceId === "explore") return "explore"; // Explore gets its own green shimmer
    if (isContinueJourneyLoading) return "continueJourney"; // Continue Journey shimmer
    if (loadingSourceId) return "source"; // High priority
    if (loadingQuestionId) return "ask"; // Questions use same shimmer as Ask
    if (isLoadingAnswer) return "ask"; // Medium priority
    if (isLearnMoreActive) return "learnMore"; // Lowest priority
    return "none";
  };

  const activeShimmer = getActiveShimmer();

  // Update local state when data.isExpanded changes or for Continue Journey nodes
  useEffect(() => {
    if (data.isExpanded !== undefined) {
      setIsExpanded(data.isExpanded);
    }
    // Force expansion for Continue Journey nodes
    if (isContinueJourney) {
      setIsExpanded(true);
    }
  }, [data.isExpanded, isContinueJourney]);

  const handleHighlight = (selectedText) => {
    if (selectedText && handleTextHighlight) {
      // Check authentication before allowing Learn More
      if (!isAuthenticated) {
        setShowLoginModal(true);
        return;
      }
      // Call the actual highlight handler (this will trigger shimmer separately)
      handleTextHighlight(selectedText, data);
    }
  };

  // Energy Flow configurations for each action type - Extended duration for better UX
  const energyFlowConfig = {
    learnMore: {
      type: "energy-flow-left-right",
      duration: 3000, // 3 seconds to see new node creation
    },
    ask: {
      type: "energy-flow-top-bottom",
      duration: 3000, // 3 seconds to see new node creation
    },
    source: {
      type: "energy-flow-right-left",
      duration: 3000, // 3 seconds to see new node creation
    },
    reflect: {
      type: "energy-flow-left-right",
      duration: 3000, // 3 seconds to see new node creation
    },
    explore: {
      type: "energy-flow-explore-green",
      duration: 3000, // 3 seconds to see new node creation
    },
    continueJourney: {
      type: "energy-flow-top-bottom",
      duration: 3000, // 3 seconds to see new node creation
    },
  };

  // Smart shimmer direction mapping
  const getShimmerDirection = (shimmerType) => {
    const shimmerMap = {
      "left-to-right": "energy-flow-left-right",
      "right-to-left": "energy-flow-right-left",
      "top-to-bottom": "energy-flow-top-bottom",
      "bottom-to-top": "energy-flow-explore-green", // Use green horizontal for explore
    };
    return shimmerMap[shimmerType] || "energy-flow-left-right";
  };

  // Instant Energy Flow trigger function with smart shimmer direction
  const triggerLearnMoreShimmer = useCallback(
    (shimmerDirection = "left-to-right") => {
      const element = contentRef.current;
      if (!element) return;

      // Set active shimmer state INSTANTLY
      setIsLearnMoreActive(true);

      // Get appropriate shimmer class based on direction
      const shimmerClass = getShimmerDirection(shimmerDirection);

      // Add energy flow classes with NO delay - instant start
      element.classList.add(
        "energy-flow-container",
        "energy-flow-active",
        shimmerClass
      );

      // Auto-cleanup after 3 seconds - enough time to see new node creation
      setTimeout(() => {
        element.classList.add("energy-flow-completing");
      }, energyFlowConfig.learnMore.duration - 500);

      setTimeout(() => {
        element.classList.remove(
          "energy-flow-container",
          "energy-flow-active",
          "energy-flow-completing",
          shimmerClass
        );
        setIsLearnMoreActive(false);
      }, energyFlowConfig.learnMore.duration);
    },
    []
  );

  // Enhanced source click handler with smart shimmer direction
  const handleSourceClickWithLoading = async (sourceType, sourceName) => {
    // Check authentication before allowing source exploration
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    const sourceId = `${sourceType}-${sourceName}`;

    // IMMEDIATELY start animation and set loading state
    setLoadingSourceId(sourceId);
    const element = contentRef.current;

    if (element) {
      // Get smart shimmer direction (sources typically go left by default but can be dynamic)
      const shimmerClass = getShimmerDirection("right-to-left"); // Default for sources

      // Start energy flow INSTANTLY - no delays
      element.classList.add(
        "energy-flow-container",
        "energy-flow-active",
        shimmerClass
      );

      // Schedule animation cleanup IMMEDIATELY - independent of API
      setTimeout(() => {
        element.classList.add("energy-flow-completing");
      }, energyFlowConfig.source.duration - 500);

      setTimeout(() => {
        element.classList.remove(
          "energy-flow-container",
          "energy-flow-active",
          "energy-flow-completing",
          shimmerClass
        );
      }, energyFlowConfig.source.duration);
    }

    // API call runs completely independently
    try {
      await handleSourceClick(sourceType, sourceName, data);
    } catch (error) {
      console.error("Source click failed:", error);
    } finally {
      // Only clear loading state when API completes
      setLoadingSourceId(null);
    }
  };

  const getPreviewText = (text) => {
    const lines = text.split("\n");
    return lines.slice(0, 5).join("\n") + (lines.length > 5 ? "..." : "");
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (customQuestion.trim() && handleCustomQuestion && !isLoadingAnswer) {
      // Check authentication before allowing custom questions
      if (!isAuthenticated) {
        setShowLoginModal(true);
        return;
      }
      // IMMEDIATELY start loading state and animation in parallel
      setIsLoadingAnswer(true);
      const element = contentRef.current;

      // Start energy flow INSTANTLY - no delays
      if (element) {
        // Ask questions typically go downward (top-to-bottom shimmer)
        const shimmerClass = getShimmerDirection("top-to-bottom");

        element.classList.add(
          "energy-flow-container",
          "energy-flow-active",
          shimmerClass
        );

        // Schedule animation cleanup IMMEDIATELY - independent of API
        setTimeout(() => {
          element.classList.add("energy-flow-completing");
        }, energyFlowConfig.ask.duration - 500);

        setTimeout(() => {
          element.classList.remove(
            "energy-flow-container",
            "energy-flow-active",
            "energy-flow-completing",
            shimmerClass
          );
        }, energyFlowConfig.ask.duration);
      }

      // API call runs completely independently
      try {
        await handleCustomQuestion(customQuestion.trim(), data);
        setCustomQuestion(""); // Clear input after successful submission
      } finally {
        // Only clear loading state when API completes
        setIsLoadingAnswer(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleQuestionSubmit(e);
    }
  };

  // Handle color change for node customization
  const handleColorChange = (colorData) => {
    if (updateNodeData && data?.nodeId) {
      updateNodeData(data.nodeId, { customColor: colorData });
    }
  };

  // Handle copy content to clipboard
  const handleCopyContent = async () => {
    if (!data.response) return;

    try {
      // Get plain text version of the content (strip markdown formatting)
      const plainText = data.response
        .replace(/\\u[\dA-F]{4}/gi, (match) =>
          String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16))
        )
        .replace(/[*_~`#]/g, '') // Basic markdown removal
        .trim();

      // Add "Powered by BibeCheck" at the bottom
      const textWithBranding = `${plainText}\n\n---\nPowered by BibeCheck`;

      await navigator.clipboard.writeText(textWithBranding);
      setIsCopied(true);

      // Reset after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  // Get header class based on custom color or default logic
  const getHeaderClass = () => {
    // Check for custom color first
    if (data?.customColor) {
      return data.customColor.value;
    }

    // Original gradient logic fallback
    if (isFirstNode) {
      return "bg-gradient-to-r from-amber-600 from-0% to-amber-600 to-90% to-yellow-500 to-100%";
    } else if (isLastNode) {
      return "bg-gradient-to-r from-emerald-600 from-0% to-emerald-600 to-90% to-green-500 to-100%";
    } else if (isSourceNode) {
      return "bg-gradient-to-r from-amber-700 via-orange-800 to-amber-900";
    } else if (activeShimmer === "source") {
      return "bg-gradient-to-r from-blue-600 via-purple-500 to-amber-700";
    } else if (activeShimmer === "ask") {
      return "bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-700";
    } else if (activeShimmer === "learnMore") {
      return "bg-gradient-to-r from-purple-500 via-violet-600 to-purple-700";
    } else if (activeShimmer === "reflect") {
      return "bg-gradient-to-r from-indigo-500 via-purple-600 to-indigo-700";
    } else if (activeShimmer === "explore") {
      return "bg-gradient-to-r from-green-500 via-teal-600 to-green-700";
    } else if (activeShimmer === "continueJourney") {
      return "bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-700";
    } else {
      return "bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800";
    }
  };

  const handleReflectAndPlay = async () => {
    if (handleReflectAndPlayAction) {
      try {
        await handleReflectAndPlayAction(data);
      } catch (error) {
        console.error("Reflect and Play action failed:", error);
      }
    }
  };

  const handleExplore = async () => {
    if (handleExploreAction) {
      try {
        await handleExploreAction(data);
      } catch (error) {
        console.error("Explore action failed:", error);
      }
    }
  };

  // Handler for suggested question clicks
  const handleSuggestedQuestion = async (question, questionIndex) => {
    if (!handleCustomQuestion || loadingQuestionId) return;

    // Check authentication before allowing suggested questions
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    const questionId = `question-${questionIndex}`;

    // IMMEDIATELY start loading state and animation
    setLoadingQuestionId(questionId);
    const element = contentRef.current;

    if (element) {
      // Questions flow downward like Ask button
      const shimmerClass = getShimmerDirection("top-to-bottom");

      element.classList.add(
        "energy-flow-container",
        "energy-flow-active",
        shimmerClass
      );

      // Schedule animation cleanup
      setTimeout(() => {
        element.classList.add("energy-flow-completing");
      }, energyFlowConfig.ask.duration - 500);

      setTimeout(() => {
        element.classList.remove(
          "energy-flow-container",
          "energy-flow-active",
          "energy-flow-completing",
          shimmerClass
        );
      }, energyFlowConfig.ask.duration);
    }

    // API call runs independently
    try {
      await handleCustomQuestion(question, data);
    } catch (error) {
      console.error("Suggested question failed:", error);
    } finally {
      setLoadingQuestionId(null);
    }
  };

  // Enhanced Reflect and Pray click handler with smart shimmer direction
  const handleReflectAndPrayWithLoading = async () => {
    // Check authentication before allowing Reflect and Pray
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    const sourceId = "reflect-and-pray";

    // IMMEDIATELY start animation and set loading state
    setLoadingSourceId(sourceId);
    const element = contentRef.current;

    if (element) {
      // Reflect and Pray typically goes left (right-to-left shimmer)
      const shimmerClass = getShimmerDirection("right-to-left");

      // Start energy flow INSTANTLY - no delays
      element.classList.add(
        "energy-flow-container",
        "energy-flow-active",
        shimmerClass
      );

      // Schedule animation cleanup IMMEDIATELY - independent of API
      setTimeout(() => {
        element.classList.add("energy-flow-completing");
      }, energyFlowConfig.reflect.duration - 500);

      setTimeout(() => {
        element.classList.remove(
          "energy-flow-container",
          "energy-flow-active",
          "energy-flow-completing",
          shimmerClass
        );
      }, energyFlowConfig.reflect.duration);
    }

    // API call runs completely independently
    try {
      await handleReflectAndPlay();
    } catch (error) {
      console.error("Reflect and Pray action failed:", error);
    } finally {
      // Only clear loading state when API completes
      setLoadingSourceId(null);
    }
  };

  // Enhanced Explore click handler with smart shimmer direction
  const handleExploreWithLoading = async () => {
    // Check authentication before allowing Explore
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    const sourceId = "explore";

    // IMMEDIATELY start animation and set loading state
    setLoadingSourceId(sourceId);
    const element = contentRef.current;

    if (element) {
      // Explore typically goes upward (bottom-to-top shimmer)
      const shimmerClass = getShimmerDirection("bottom-to-top");

      // Start energy flow INSTANTLY - no delays
      element.classList.add(
        "energy-flow-container",
        "energy-flow-active",
        shimmerClass
      );

      // Schedule animation cleanup IMMEDIATELY - independent of API
      setTimeout(() => {
        element.classList.add("energy-flow-completing");
      }, energyFlowConfig.explore.duration - 500);

      setTimeout(() => {
        element.classList.remove(
          "energy-flow-container",
          "energy-flow-active",
          "energy-flow-completing",
          shimmerClass
        );
      }, energyFlowConfig.explore.duration);
    }

    // API call runs completely independently
    try {
      await handleExplore();
    } catch (error) {
      console.error("Explore action failed:", error);
    } finally {
      // Only clear loading state when API completes
      setLoadingSourceId(null);
    }
  };

  // Custom components for ReactMarkdown - all selectable
  const markdownComponents = {
    h1: ({ children }) => (
      <h1
        className="text-xl font-semibold text-gray-900 mt-4 mb-3 select-text pr-2"
        style={{
          overflowWrap: "break-word",
          wordBreak: "normal",
          whiteSpace: "normal",
          hyphens: "none",
          width: "calc(100% - 8px)",
          maxWidth: "calc(100% - 8px)",
        }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2
        className="text-lg font-semibold text-gray-900 mt-4 mb-2 select-text pr-2"
        style={{
          overflowWrap: "break-word",
          wordBreak: "normal",
          whiteSpace: "normal",
          hyphens: "none",
          width: "calc(100% - 8px)",
          maxWidth: "calc(100% - 8px)",
        }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        className="text-base font-medium text-gray-800 mt-3 mb-2 select-text pr-2"
        style={{
          overflowWrap: "break-word",
          wordBreak: "normal",
          whiteSpace: "normal",
          hyphens: "none",
          width: "calc(100% - 8px)",
          maxWidth: "calc(100% - 8px)",
        }}>
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p
        className="mb-3 leading-relaxed text-gray-800 text-sm select-text pr-2"
        style={{
          overflowWrap: "break-word",
          wordBreak: "normal",
          whiteSpace: "normal",
          hyphens: "none",
          width: "calc(100% - 8px)",
          maxWidth: "calc(100% - 8px)",
        }}>
        {children}
      </p>
    ),
    br: () => <br />,
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900 select-text break-words">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-700 select-text break-words">
        {children}
      </em>
    ),
    ul: ({ children }) => (
      <ul className="list-disc space-y-1 my-3 ml-4 pl-4 select-text w-full min-w-0">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal space-y-1 my-3 ml-4 pl-4 select-text w-full min-w-0">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li
        className="mb-1 text-sm text-gray-800 pr-3 mr-2 select-text w-full max-w-full min-w-0"
        style={{
          overflowWrap: "break-word",
          wordBreak: "normal",
          whiteSpace: "normal",
          hyphens: "none",
          width: "calc(100% - 12px)",
          maxWidth: "calc(100% - 12px)",
        }}>
        {children}
      </li>
    ),
    code: ({ children }) => (
      <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono select-text break-words">
        {children}
      </code>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary/30 pl-4 my-3 italic text-gray-700 select-text break-words">
        {children}
      </blockquote>
    ),
  };

  // Handle click on any part of the node to bring it to front
  const handleNodeClick = useCallback((event) => {
    // Only trigger if we're not clicking on interactive elements
    const target = event.target;
    const isInteractiveElement = target.closest('button, input, textarea, select, a, [contenteditable], form');

    if (!isInteractiveElement && bringNodeToFront && data?.nodeId) {
      console.log("🎯 Node content clicked, bringing to front:", data.nodeId);
      bringNodeToFront(data.nodeId);
    }
  }, [bringNodeToFront, data?.nodeId]);

  // Handle double-click/double-tap on header to zoom in
  const handleHeaderDoubleClick = useCallback((event) => {
    // Prevent default behavior and event propagation
    event.preventDefault();
    event.stopPropagation();

    // Only trigger if we're not clicking on interactive elements
    const target = event.target;
    const isInteractiveElement = target.closest('button, input, textarea, select, a, [contenteditable], form');

    if (!isInteractiveElement && focusNode && data?.nodeId && reactFlowInstance) {
      console.log("🔍 Header double-clicked, zooming to node:", data.nodeId);

      // Get the LIVE node data from ReactFlow instance (not stale state)
      const liveNode = reactFlowInstance.getNode(data.nodeId);

      if (liveNode) {
        console.log("📍 Live node data:", liveNode);
        console.log("📐 Live node measured:", liveNode.measured);

        // Calculate the TRUE CENTER using measured dimensions
        let centerX, centerY;

        if (liveNode.measured?.width && liveNode.measured?.height) {
          // Use actual measured dimensions
          centerX = liveNode.position.x + liveNode.measured.width / 2;
          centerY = liveNode.position.y + liveNode.measured.height / 2;
          console.log("✅ Using measured dimensions:", liveNode.measured);
        } else {
          // Fallback to estimated dimensions
          centerX = liveNode.position.x + 224; // 448px / 2
          centerY = liveNode.position.y + 200;
          console.log("⚠️ Using fallback dimensions");
        }

        console.log("🎯 Calculated center point:", { x: centerX, y: centerY });

        // Call ReactFlow's setCenter directly with the calculated center point
        reactFlowInstance.setCenter(centerX, centerY, {
          zoom: 1.5,
          duration: 800
        });
      } else {
        // Fallback if node not found (shouldn't happen)
        console.warn("⚠️ Live node not found, using fallback");
        focusNode(data.nodeId, null, 800, 1.5);
      }
    }
  }, [focusNode, data?.nodeId, reactFlowInstance]);

  return (
    <motion.div
      data-id={data?.nodeId}
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{
        opacity: 1,
        scale: isFirstNode ? 1.0 : 1, // Keep first node at normal size
        y: 0,
      }}
      transition={{
        duration: 0.4,
        ease: "easeOut",
        delay: 0.1,
      }}
      whileHover={{
        boxShadow: isFirstNode
          ? "0 10px 30px rgba(212, 175, 55, 0.3)"
          : "0 8px 25px rgba(0,0,0,0.15)",
      }}
      onClick={handleNodeClick}
      className={`relative bg-white rounded-2xl shadow-lg p-6 pr-8 w-full max-w-md min-w-0 transition-all duration-300 ${
        isFirstNode
          ? "border-4 border-amber-400"
          : isLastNode
          ? "border-4 border-emerald-400"
          : isSummary
          ? "border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50"
          : "border-2 border-gray-200 hover:border-primary/30"
      }`}
      style={{
        overflowWrap: "break-word",
        overflow: "visible", // Ensure labels above nodes are not clipped
        wordBreak: "normal",
        wordWrap: "break-word",
        whiteSpace: "normal",
        hyphens: "none",
        boxShadow: isFirstNode
          ? `0 0 25px rgba(212, 175, 55, 0.4)` // Consistent prominent golden glow for root node
          : isLastNode
          ? `0 0 ${zoom < 0.6 ? "30px" : "20px"} rgba(16, 185, 129, ${
              zoom < 0.6 ? "0.5" : "0.3"
            })`
          : undefined,
      }}
      ref={contentRef}>
      {/* ReactFlow Handles for connections - All four sides for dynamic connections */}
      {/* Target handles (inputs) on all sides */}
      <Handle
        type="target"
        position={Position.Left}
        id="target-left"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="target-top"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="target-right"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="target-bottom"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />

      {/* Source handles (outputs) on all sides */}
      <Handle
        type="source"
        position={Position.Left}
        id="source-left"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="source-top"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-right"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom"
        style={{
          background: "#d4af37",
          border: "2px solid #fff",
          width: "12px",
          height: "12px",
        }}
      />

      {/* First Node Indicators */}
      {isFirstNode && (
        <>
          {/* Gentle pulse effect overlay */}
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            animate={{
              opacity: [0, 0.1, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              background:
                "radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)",
              zIndex: -1,
            }}
          />

          {/* "START" badge - positioned above node */}
          <motion.div
            className="absolute -top-14 left-0 right-0 flex justify-center z-[999]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ pointerEvents: "none" }}>
            <div className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border-2 border-white">
              START
            </div>
          </motion.div>
        </>
      )}

      {/* Last Node Indicators */}
      {isLastNode && !isFirstNode && (
        <>
          {/* Gentle green pulse effect overlay */}
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            animate={{
              opacity: [0, 0.1, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              background:
                "radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)",
              zIndex: -1,
            }}
          />

          {/* "CURRENT" badge - positioned above node */}
          <motion.div
            className="absolute -top-14 left-0 right-0 flex justify-center z-[999]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ pointerEvents: "none" }}>
            <div className="bg-gradient-to-r from-emerald-500 to-green-400 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl border-2 border-white">
              CURRENT
            </div>
          </motion.div>
        </>
      )}

      {/* Sequence Number Indicators for Intermediate Nodes */}
      {!isFirstNode && !isLastNode && data?.sequenceNumber && (
        <motion.div
          className="absolute -top-12 right-0 z-[999]"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ pointerEvents: "none" }}>
          <div className="bg-gray-100 text-gray-500 text-xs font-medium px-3 py-2 rounded-full shadow-sm border border-gray-400 min-w-[28px] text-center">
            {data.sequenceNumber}
          </div>
        </motion.div>
      )}

      {/* Beautiful Gradient Question Header - Only this part is draggable */}
      <motion.div
        className={`drag-handle mb-4 -ml-6 -mr-8 -mt-6 pl-6 pr-8 py-4 rounded-t-lg cursor-move transition-all duration-500 ${getHeaderClass()}`}
        onDoubleClick={handleHeaderDoubleClick}
        animate={{
          boxShadow: isFirstNode
            ? [
                "0 4px 20px rgba(212, 175, 55, 0.2)",
                "0 4px 20px rgba(212, 175, 55, 0.4)",
                "0 4px 20px rgba(212, 175, 55, 0.2)",
              ]
            : activeShimmer === "source"
            ? [
                "0 4px 20px rgba(212, 175, 55, 0.1)",
                "0 4px 20px rgba(212, 175, 55, 0.3)",
                "0 4px 20px rgba(212, 175, 55, 0.1)",
              ]
            : activeShimmer === "ask"
            ? [
                "0 4px 20px rgba(45, 95, 234, 0.1)",
                "0 4px 20px rgba(45, 95, 234, 0.3)",
                "0 4px 20px rgba(45, 95, 234, 0.1)",
              ]
            : activeShimmer === "learnMore"
            ? [
                "0 4px 20px rgba(107, 70, 193, 0.1)",
                "0 4px 20px rgba(107, 70, 193, 0.3)",
                "0 4px 20px rgba(107, 70, 193, 0.1)",
              ]
            : activeShimmer === "reflect"
            ? [
                "0 4px 20px rgba(99, 102, 241, 0.1)",
                "0 4px 20px rgba(99, 102, 241, 0.3)",
                "0 4px 20px rgba(99, 102, 241, 0.1)",
              ]
            : activeShimmer === "explore"
            ? [
                "0 4px 20px rgba(34, 197, 94, 0.1)",
                "0 4px 20px rgba(34, 197, 94, 0.3)",
                "0 4px 20px rgba(34, 197, 94, 0.1)",
              ]
            : activeShimmer === "continueJourney"
            ? [
                "0 4px 20px rgba(147, 51, 234, 0.1)",
                "0 4px 20px rgba(147, 51, 234, 0.3)",
                "0 4px 20px rgba(147, 51, 234, 0.1)",
              ]
            : "0 4px 20px rgba(0, 0, 0, 0.1)",
        }}
        transition={{
          boxShadow: {
            duration: 2,
            repeat: activeShimmer !== "none" || isFirstNode ? Infinity : 0,
          },
        }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            {isSummary && (
              <Sparkles size={18} className="mr-3 text-yellow-300" />
            )}
            {isSourceNode &&
              (data.sourceType === "book" ? (
                <BookOpen size={18} className="mr-3 text-orange-200" />
              ) : (
                <FileText size={18} className="mr-3 text-orange-200" />
              ))}
            {/* {isReflectAndPray && (
              <Heart size={18} className="mr-3 text-pink-200" />
            )} */}
            {isExplore && <span className="mr-3 text-white text-xl">✨</span>}
            <h3
              className={`font-semibold text-base leading-relaxed select-none ${
                isFirstNode
                  ? "text-white drop-shadow-md" // Enhanced visibility on golden background
                  : isLastNode
                  ? "text-white drop-shadow-md" // Enhanced visibility on green background
                  : "text-white"
              }`}>
              {data.question}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Copy Button */}
            {data.response && (
              <button
                onClick={handleCopyContent}
                className="nodrag text-white/80 hover:text-white transition-all duration-200 hover:scale-110"
                title={isCopied ? "Copied!" : "Copy content"}>
                {isCopied ? (
                  <Check size={18} className="text-green-300" />
                ) : (
                  <Copy size={18} />
                )}
              </button>
            )}
            {/* Color Picker - Only show for regular nodes (not START or CURRENT) */}
            {!isFirstNode && !isLastNode && (
              <ColorPicker
                currentColor={data?.customColor}
                onColorChange={handleColorChange}
                disabled={false}
              />
            )}
            {data.response && data.response.split("\n").length > 5 && (
              <button
                onClick={() => {
                  const newExpandedState = !isExpanded;
                  setIsExpanded(newExpandedState);
                  // Update global state
                  if (updateNodeExpansion) {
                    updateNodeExpansion(data.nodeId, newExpandedState);
                  }
                }}
                className="nodrag ml-2 text-white/80 hover:text-white transition-colors">
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Response Content with Enhanced Animation - Not draggable */}
      <motion.div
        className="nodrag mb-4 cursor-text text-sm"
        style={{
          userSelect: "text",
          WebkitUserSelect: "text",
          MozUserSelect: "text",
          msUserSelect: "text",
        }}
        animate={{
          height: isExpanded ? "auto" : "fit-content",
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}>
        <motion.div
          initial={false}
          animate={{
            opacity: 1,
          }}
          transition={{
            duration: 0.2,
            delay: isExpanded ? 0.1 : 0,
          }}
          style={{
            userSelect: "text",
            WebkitUserSelect: "text",
            MozUserSelect: "text",
            msUserSelect: "text",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflowWrap: "break-word",
            wordBreak: "normal",
            whiteSpace: "normal",
            hyphens: "none",
          }}
          className="selectable-content w-full min-w-0 max-w-full">
          <HighlightHandler
            onHighlight={handleHighlight}
            triggerShimmer={triggerLearnMoreShimmer}
            isEnabled={true}>
            {data.response ? (
              isContinueJourney ? (
                // Special styling for Continue Journey spiritual message
                <div className="text-center py-6 px-4">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}>
                    {/* Dove emoji with gentle animation */}
                    <motion.div
                      className="text-5xl mb-4"
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}>
                      🕊️
                    </motion.div>

                    {/* Main heading with gradient */}
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4 leading-relaxed">
                      Your Spiritual Journey Continues
                    </h2>

                    {/* Body text with warm styling */}
                    <p className="text-gray-700 text-base leading-relaxed max-w-md mx-auto break-words">
                      Discover divine wisdom for life's challenges. Let God's
                      Word guide your path as you explore these meaningful
                      topics:
                    </p>
                  </motion.div>
                </div>
              ) : isReflectAndPray ? (
                // Special styling for Reflect and Pray responses
                <div className="text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}>
                    {/* Praying hands emoji with gentle animation */}
                    <motion.div
                      className="text-5xl mb-6 mt-10"
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}>
                      🙏
                    </motion.div>

                    {/* Enhanced markdown with special styling for sections */}
                    <div className="text-left break-words">
                      <ReactMarkdown
                        components={{
                          ...markdownComponents,
                          h2: ({ children }) => {
                            const text = String(children);
                            if (text.includes("Reflect")) {
                              return (
                                <div className="flex items-center gap-2 text-xl font-bold mt-6 mb-4 pb-2 border-b border-gray-200">
                                  {/* <span className="text-2xl">🤔</span> */}
                                  <h2>{children}</h2>
                                </div>
                              );
                            } else if (text.includes("Practical Tips")) {
                              return (
                                <div className="flex items-center gap-2 text-xl font-bold mt-6 mb-4 pb-2 border-b border-gray-200">
                                  {/* <span className="text-2xl">💡</span> */}
                                  <h2>{children}</h2>
                                </div>
                              );
                            } else if (text.includes("Pray")) {
                              return (
                                <div className="flex items-center gap-2 text-xl font-bold mt-6 mb-4 pb-2 border-b border-gray-200">
                                  {/* <span className="text-2xl">🙏</span> */}
                                  <h2>{children}</h2>
                                </div>
                              );
                            }
                            return (
                              <h2 className="text-lg font-semibold text-gray-900 mt-4 mb-2 select-text break-words">
                                {children}
                              </h2>
                            );
                          },
                          p: ({ children }) => {
                            // Check if this paragraph comes after "Pray" heading (prayer text)
                            const text = String(children);
                            if (text.length > 100 && text.includes("Lord")) {
                              return (
                                <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/30 border-l-4 border-indigo-300 p-4 rounded-r-lg my-4">
                                  <p className="italic text-gray-800 text-base leading-relaxed select-text break-words">
                                    {children}
                                  </p>
                                </div>
                              );
                            }
                            return (
                              <p className="mb-3 leading-relaxed text-gray-800 text-sm select-text">
                                {children}
                              </p>
                            );
                          },
                          ol: ({ children }) => (
                            <ol className="list-decimal space-y-3 my-4 ml-6 pl-2 select-text">
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li className="mb-2 text-base text-gray-800 leading-relaxed select-text">
                              {children}
                            </li>
                          ),
                        }}
                        breaks={true}>
                        {isExpanded
                          ? data.response.replace(/\\u[\dA-F]{4}/gi, (match) =>
                              String.fromCharCode(
                                parseInt(match.replace(/\\u/g, ""), 16)
                              )
                            )
                          : getPreviewText(data.response).replace(
                              /\\u[\dA-F]{4}/gi,
                              (match) =>
                                String.fromCharCode(
                                  parseInt(match.replace(/\\u/g, ""), 16)
                                )
                            )}
                      </ReactMarkdown>
                    </div>
                  </motion.div>
                </div>
              ) : isExplore ? (
                // Special styling for Explore (I'm feeling blessed) responses
                <div className="text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}>
                    {/* Sparkles/Blessed emoji with gentle animation */}
                    <motion.div
                      className="text-5xl mb-6 mt-10"
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}>
                      ✨
                    </motion.div>

                    {/* Regular markdown for content */}
                    <div className="text-left break-words">
                      <ReactMarkdown
                        components={markdownComponents}
                        breaks={true}>
                        {isExpanded
                          ? data.response.replace(/\\u[\dA-F]{4}/gi, (match) =>
                              String.fromCharCode(
                                parseInt(match.replace(/\\u/g, ""), 16)
                              )
                            )
                          : getPreviewText(data.response).replace(
                              /\\u[\dA-F]{4}/gi,
                              (match) =>
                                String.fromCharCode(
                                  parseInt(match.replace(/\\u/g, ""), 16)
                                )
                            )}
                      </ReactMarkdown>
                    </div>
                  </motion.div>
                </div>
              ) : isExplore ? (
                // Special styling for Explore/Discover responses
                <div className="text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}>
                    {/* Sparkles emoji with gentle animation */}
                    <motion.div
                      className="text-6xl mb-6 mt-8"
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}>
                      ✨
                    </motion.div>

                    {/* Enhanced markdown with special styling for sections */}
                    <div className="text-left break-words">
                      <ReactMarkdown
                        components={{
                          ...markdownComponents,
                          h1: ({ children }) => (
                            <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-4 select-text break-words">
                              {children}
                            </h1>
                          ),
                          h2: ({ children }) => {
                            const text = String(children);
                            // Special styling for common Discover section headings
                            if (
                              text.includes("Story") ||
                              text.includes("Key Lessons") ||
                              text.includes("Practical Applications") ||
                              text.includes("Key Takeaways")
                            ) {
                              return (
                                <div className="mt-6 mb-4">
                                  <h2 className="text-xl font-bold text-green-700 mb-2 break-words">
                                    {children}
                                  </h2>
                                  <hr className="border-t border-green-200" />
                                </div>
                              );
                            }
                            return (
                              <h2 className="text-lg font-semibold text-gray-900 mt-4 mb-2 select-text break-words">
                                {children}
                              </h2>
                            );
                          },
                          ul: ({ children }) => (
                            <ul className="space-y-3 my-4 ml-2 select-text">
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="list-decimal space-y-3 my-4 ml-6 pl-2 select-text">
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li className="mb-2 text-base text-gray-800 leading-relaxed select-text">
                              {children}
                            </li>
                          ),
                        }}
                        breaks={true}>
                        {isExpanded
                          ? data.response.replace(/\\u[\dA-F]{4}/gi, (match) =>
                              String.fromCharCode(
                                parseInt(match.replace(/\\u/g, ""), 16)
                              )
                            )
                          : getPreviewText(data.response).replace(
                              /\\u[\dA-F]{4}/gi,
                              (match) =>
                                String.fromCharCode(
                                  parseInt(match.replace(/\\u/g, ""), 16)
                                )
                            )}
                      </ReactMarkdown>
                    </div>
                  </motion.div>
                </div>
              ) : (
                <ReactMarkdown components={markdownComponents} breaks={true}>
                  {isExpanded
                    ? data.response.replace(/\\u[\dA-F]{4}/gi, (match) =>
                        String.fromCharCode(
                          parseInt(match.replace(/\\u/g, ""), 16)
                        )
                      )
                    : getPreviewText(data.response).replace(
                        /\\u[\dA-F]{4}/gi,
                        (match) =>
                          String.fromCharCode(
                            parseInt(match.replace(/\\u/g, ""), 16)
                          )
                      )}
                </ReactMarkdown>
              )
            ) : (
              <div className="text-gray-500 italic">Loading response...</div>
            )}
          </HighlightHandler>
        </motion.div>
      </motion.div>

      {/* More/Less Button Section (Hidden for Continue Journey nodes) */}
      {data.response && !isContinueJourney && (
        <motion.div
          className="flex justify-end mb-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}>
          <button
            onClick={() => {
              const newExpandedState = !isExpanded;
              setIsExpanded(newExpandedState);
              // Update global state
              if (updateNodeExpansion) {
                updateNodeExpansion(data.nodeId, newExpandedState);
              }
            }}
            className="nodrag flex items-center gap-1 px-3 py-1 text-sm text-primary hover:text-primary-dark font-medium transition-colors duration-200 hover:bg-primary/5 rounded-md">
            <span>{isExpanded ? "Less" : "More"}</span>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </motion.div>
      )}

      {/* Source Section - Shows referenced books and verses */}
      {isExpanded &&
        (data.booksMentioned?.length > 0 ||
          data.versesReferenced?.length > 0) && (
          <motion.div
            className={`border-t pt-2 mt-3 mb-2 transition-all duration-500 ${
              activeShimmer === "source"
                ? "border-amber-200/60 bg-gradient-to-r from-amber-50/30 to-orange-50/20"
                : "border-gray-100"
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: activeShimmer === "source" ? 1.02 : 1,
            }}
            transition={{
              delay: 0.4,
              scale: { duration: 0.3 },
            }}>
            <motion.div
              className={`text-xs mb-1 transition-all duration-300 ${
                activeShimmer === "source"
                  ? "text-amber-600 font-medium"
                  : "text-gray-500"
              }`}
              animate={{
                scale: activeShimmer === "source" ? [1, 1.05, 1] : 1,
              }}
              transition={{
                scale: {
                  duration: 1.5,
                  repeat: activeShimmer === "source" ? Infinity : 0,
                },
              }}>
              📖 Sources:
              {activeShimmer === "source" && (
                <motion.span
                  className="ml-1 text-amber-500"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}>
                  ✨
                </motion.span>
              )}
            </motion.div>
            <div className="flex flex-wrap gap-2">
              {data.booksMentioned?.map((book, index) => {
                const sourceId = `book-${book}`;
                const isThisSourceLoading = loadingSourceId === sourceId;

                return (
                  <motion.button
                    key={`book-${index}`}
                    onClick={() => handleSourceClickWithLoading("book", book)}
                    disabled={isThisSourceLoading}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-all duration-300 cursor-pointer nodrag ${
                      isThisSourceLoading
                        ? "bg-gray-100 text-gray-500 border-gray-300 cursor-wait"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-500 hover:scale-105"
                    }`}
                    title={
                      isThisSourceLoading
                        ? "Loading..."
                        : `Learn more about ${book}`
                    }
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      scale: isThisSourceLoading
                        ? [1, 1.02, 1]
                        : activeShimmer === "source"
                        ? 0.98
                        : 1,
                      opacity:
                        activeShimmer === "source" && !isThisSourceLoading
                          ? 0.6
                          : 1,
                    }}
                    transition={{
                      scale: {
                        duration: 0.6,
                        repeat: isThisSourceLoading ? Infinity : 0,
                      },
                      opacity: { duration: 0.3 },
                    }}>
                    {isThisSourceLoading ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        <span>📚 {book}</span>
                      </>
                    ) : (
                      <>📚 {book}</>
                    )}
                  </motion.button>
                );
              })}
              {data.versesReferenced?.map((verse, index) => {
                const sourceId = `verse-${verse}`;
                const isThisSourceLoading = loadingSourceId === sourceId;

                return (
                  <motion.button
                    key={`verse-${index}`}
                    onClick={() => handleSourceClickWithLoading("verse", verse)}
                    disabled={isThisSourceLoading}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-all duration-300 cursor-pointer nodrag ${
                      isThisSourceLoading
                        ? "bg-gray-100 text-gray-500 border-gray-300 cursor-wait"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-500 hover:scale-105"
                    }`}
                    title={
                      isThisSourceLoading
                        ? "Loading..."
                        : `Learn more about ${verse}`
                    }
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      scale: isThisSourceLoading
                        ? [1, 1.02, 1]
                        : activeShimmer === "source"
                        ? 0.98
                        : 1,
                      opacity:
                        activeShimmer === "source" && !isThisSourceLoading
                          ? 0.6
                          : 1,
                    }}
                    transition={{
                      scale: {
                        duration: 0.6,
                        repeat: isThisSourceLoading ? Infinity : 0,
                      },
                      opacity: { duration: 0.3 },
                    }}>
                    {isThisSourceLoading ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        <span>📖 {verse}</span>
                      </>
                    ) : (
                      <>📖 {verse}</>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

      {/* People Also Searched Section - Controlled by env variable */}
      {isExpanded &&
        data.actions &&
        data.actions.length > 0 &&
        !isReflection &&
        !isContinueJourney &&
        featureFlags.quickActions &&
        process.env.REACT_APP_SHOW_PEOPLE_ALSO_SEARCHED === "true" && (
          <motion.div
            className={`relative border-t pt-3 mt-3 mb-3 transition-all duration-700 rounded-lg ${
              loadingQuestionId
                ? "border-blue-200/60 bg-gradient-to-br from-blue-50/40 via-purple-50/30 to-pink-50/20"
                : "border-gray-100 bg-gradient-to-br from-gray-50/20 to-transparent"
            }`}
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: loadingQuestionId ? 1.01 : 1,
            }}
            transition={{
              delay: 0.4,
              scale: { duration: 0.4, ease: "easeOut" },
            }}>
            {/* Section Header with Badge */}
            <motion.div
              className="flex items-center justify-between mb-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}>
              <motion.div
                className={`text-xs font-medium transition-all duration-500 flex items-center gap-2 ${
                  loadingQuestionId ? "text-blue-600" : "text-gray-600"
                }`}
                animate={{
                  scale: loadingQuestionId ? [1, 1.03, 1] : 1,
                }}
                transition={{
                  scale: {
                    duration: 2,
                    repeat: loadingQuestionId ? Infinity : 0,
                  },
                }}>
                <span className="text-sm">🔥</span>
                <span>People also searched:</span>
                {loadingQuestionId && (
                  <motion.span
                    className="text-blue-500"
                    initial={{ opacity: 0, rotate: 0 }}
                    animate={{ opacity: 1, rotate: 360 }}
                    transition={{ duration: 0.5 }}>
                    ✨
                  </motion.span>
                )}
              </motion.div>
              <motion.span
                className="px-2 py-0.5 bg-blue-100/60 text-blue-600 text-xs rounded-full font-medium"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 500 }}>
                {data.actions.length}
              </motion.span>
            </motion.div>

            {/* Questions List with Staggered Animation */}
            <div className="space-y-2.5">
              {data.actions.map((question, index) => {
                const questionId = `question-${index}`;
                const isThisQuestionLoading = loadingQuestionId === questionId;

                return (
                  <motion.button
                    key={questionId}
                    onClick={() => handleSuggestedQuestion(question, index)}
                    disabled={loadingQuestionId !== null}
                    className={`group relative w-full text-left px-4 py-3 rounded-xl text-xs border transition-all duration-300 cursor-pointer nodrag overflow-hidden ${
                      isThisQuestionLoading
                        ? "bg-gradient-to-r from-blue-100 via-purple-50 to-pink-100 text-blue-700 border-purple-300 cursor-wait shadow-lg shadow-purple-200/50"
                        : loadingQuestionId
                        ? "bg-gray-50/50 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                        : "bg-white hover:bg-gradient-to-br hover:from-blue-50/40 hover:via-purple-50/30 hover:to-pink-50/40 text-gray-700 border-gray-200 hover:border-purple-300 hover:shadow-md hover:shadow-purple-200/20"
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.5 + index * 0.1,
                      duration: 0.4,
                      ease: "easeOut",
                    }}
                    whileHover={
                      !loadingQuestionId
                        ? {
                            scale: 1.02,
                            transition: { duration: 0.2 },
                          }
                        : {}
                    }
                    whileTap={!loadingQuestionId ? { scale: 0.98 } : {}}>
                    {/* Ripple Effect Background */}
                    {isThisQuestionLoading && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-200/20 to-blue-300/20"
                        initial={{ scale: 0, opacity: 1 }}
                        animate={{ scale: 2, opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                    )}

                    <div className="relative flex items-start gap-3">
                      {/* Icon with Hover Animation */}
                      <motion.span
                        className="text-lg mt-0.5 transition-transform duration-300"
                        animate={
                          isThisQuestionLoading
                            ? {
                                rotate: [0, 360],
                              }
                            : {}
                        }
                        transition={{
                          duration: 1,
                          repeat: isThisQuestionLoading ? Infinity : 0,
                          ease: "linear",
                        }}
                        whileHover={
                          !loadingQuestionId
                            ? {
                                rotate: 15,
                                scale: 1.2,
                              }
                            : {}
                        }>
                        {isThisQuestionLoading ? (
                          <Loader2
                            size={16}
                            className="animate-spin text-purple-600"
                          />
                        ) : (
                          <span className="group-hover:drop-shadow-sm">
                            {index === 0 ? "🤔" : index === 1 ? "💭" : "💡"}
                          </span>
                        )}
                      </motion.span>

                      {/* Question Text */}
                      <span className="flex-1 font-medium leading-relaxed text-sm">
                        {question}
                      </span>

                      {/* Type Badge - Color Dot */}
                      {!loadingQuestionId && (
                        <motion.span
                          className={`absolute -top-1 -right-1 w-2 h-2 rounded-full group-hover:scale-150 transition-transform duration-300 ${
                            index === 0
                              ? "bg-purple-400"
                              : index === 1
                              ? "bg-blue-400"
                              : "bg-green-400"
                          }`}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            delay: 0.6 + index * 0.1,
                            type: "spring",
                          }}
                        />
                      )}
                    </div>

                    {/* Hover Glow Effect */}
                    {!loadingQuestionId && (
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-purple-400/5 to-purple-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

      {/* Continue Journey Section - Always shown for Continue Journey nodes */}
      {isExpanded &&
        data.actions &&
        data.actions.length > 0 &&
        !isReflection &&
        isContinueJourney &&
        featureFlags.quickActions && (
          <motion.div
            className={`relative border-t pt-3 mt-3 mb-3 transition-all duration-700 rounded-lg ${
              loadingQuestionId
                ? "border-blue-200/60 bg-gradient-to-br from-blue-50/40 via-purple-50/30 to-pink-50/20"
                : "border-gray-100 bg-gradient-to-br from-gray-50/20 to-transparent"
            }`}
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: loadingQuestionId ? 1.01 : 1,
            }}
            transition={{
              delay: 0.4,
              scale: { duration: 0.4, ease: "easeOut" },
            }}>
            {/* Section Header with Badge */}
            <motion.div
              className="flex items-center justify-between mb-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}>
              <motion.div
                className={`text-xs font-medium transition-all duration-500 flex items-center gap-2 ${
                  loadingQuestionId ? "text-blue-600" : "text-gray-600"
                }`}
                animate={{
                  scale: loadingQuestionId ? [1, 1.03, 1] : 1,
                }}
                transition={{
                  scale: {
                    duration: 2,
                    repeat: loadingQuestionId ? Infinity : 0,
                  },
                }}>
                <span className="text-sm">🌟</span>
                <span>Choose your next topic:</span>
                {loadingQuestionId && (
                  <motion.span
                    className="text-blue-500"
                    initial={{ opacity: 0, rotate: 0 }}
                    animate={{ opacity: 1, rotate: 360 }}
                    transition={{ duration: 0.5 }}>
                    ✨
                  </motion.span>
                )}
              </motion.div>
              <motion.span
                className="px-2 py-0.5 bg-blue-100/60 text-blue-600 text-xs rounded-full font-medium"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 500 }}>
                {data.actions.length}
              </motion.span>
            </motion.div>

            {/* Questions List with Staggered Animation */}
            <div className="space-y-2.5">
              {data.actions.map((question, index) => {
                const questionId = `question-${index}`;
                const isThisQuestionLoading = loadingQuestionId === questionId;

                return (
                  <motion.button
                    key={questionId}
                    onClick={() => handleSuggestedQuestion(question, index)}
                    disabled={loadingQuestionId !== null}
                    className={`group relative w-full text-left px-4 py-3 rounded-xl text-xs border transition-all duration-300 cursor-pointer nodrag overflow-hidden ${
                      isThisQuestionLoading
                        ? "bg-gradient-to-r from-blue-100 via-purple-50 to-pink-100 text-blue-700 border-purple-300 cursor-wait shadow-lg shadow-purple-200/50"
                        : loadingQuestionId
                        ? "bg-gray-50/50 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                        : "bg-white hover:bg-gradient-to-br hover:from-blue-50/40 hover:via-purple-50/30 hover:to-pink-50/40 text-gray-700 border-gray-200 hover:border-purple-300 hover:shadow-md hover:shadow-purple-200/20"
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.5 + index * 0.1,
                      duration: 0.4,
                      ease: "easeOut",
                    }}
                    whileHover={
                      !loadingQuestionId
                        ? {
                            scale: 1.02,
                            transition: { duration: 0.2 },
                          }
                        : {}
                    }
                    whileTap={!loadingQuestionId ? { scale: 0.98 } : {}}>
                    {/* Ripple Effect Background */}
                    {isThisQuestionLoading && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-200/20 to-blue-300/20"
                        initial={{ scale: 0, opacity: 1 }}
                        animate={{ scale: 2, opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                    )}

                    <div className="relative flex items-start gap-3">
                      {/* Icon with Hover Animation */}
                      <motion.span
                        className="text-lg mt-0.5 transition-transform duration-300"
                        animate={
                          isThisQuestionLoading
                            ? {
                                rotate: [0, 360],
                              }
                            : {}
                        }
                        transition={{
                          duration: 1,
                          repeat: isThisQuestionLoading ? Infinity : 0,
                          ease: "linear",
                        }}
                        whileHover={
                          !loadingQuestionId
                            ? {
                                rotate: 15,
                                scale: 1.2,
                              }
                            : {}
                        }>
                        {isThisQuestionLoading ? (
                          <Loader2
                            size={16}
                            className="animate-spin text-purple-600"
                          />
                        ) : (
                          <span className="group-hover:drop-shadow-sm">
                            {index === 0 ? "🤔" : index === 1 ? "💭" : "💡"}
                          </span>
                        )}
                      </motion.span>

                      {/* Question Text */}
                      <span className="flex-1 font-medium leading-relaxed text-sm">
                        {question}
                      </span>

                      {/* Type Badge - Color Dot */}
                      {!loadingQuestionId && (
                        <motion.span
                          className={`absolute -top-1 -right-1 w-2 h-2 rounded-full group-hover:scale-150 transition-transform duration-300 ${
                            index === 0
                              ? "bg-purple-400"
                              : index === 1
                              ? "bg-blue-400"
                              : "bg-green-400"
                          }`}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            delay: 0.6 + index * 0.1,
                            type: "spring",
                          }}
                        />
                      )}
                    </div>

                    {/* Hover Glow Effect */}
                    {!loadingQuestionId && (
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-purple-400/5 to-purple-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

      {/* Quick Actions Section (Hidden for reflection responses and Continue Journey nodes) */}
      {isExpanded &&
        !isReflection &&
        !isContinueJourney &&
        featureFlags.quickActions && (
          <motion.div
            className={`border-t pt-2 mt-3 mb-2 transition-all duration-500 ${
              activeShimmer === "reflect"
                ? "border-indigo-200/60 bg-gradient-to-r from-indigo-50/30 to-purple-50/20"
                : activeShimmer === "explore"
                ? "border-green-200/60 bg-gradient-to-r from-green-50/30 to-emerald-50/20"
                : "border-gray-100"
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              scale:
                activeShimmer === "reflect" || activeShimmer === "explore"
                  ? 1.02
                  : 1,
            }}
            transition={{
              delay: 0.6,
              scale: { duration: 0.3 },
            }}>
            <motion.div
              className={`text-xs mb-1 transition-all duration-300 ${
                activeShimmer === "reflect"
                  ? "text-indigo-600 font-medium"
                  : activeShimmer === "explore"
                  ? "text-green-600 font-medium"
                  : "text-gray-500"
              }`}
              animate={{
                scale:
                  activeShimmer === "reflect" || activeShimmer === "explore"
                    ? [1, 1.05, 1]
                    : 1,
              }}
              transition={{
                scale: {
                  duration: 1.5,
                  repeat:
                    activeShimmer === "reflect" || activeShimmer === "explore"
                      ? Infinity
                      : 0,
                },
              }}>
              ⚡ Quick Actions:
              {(activeShimmer === "reflect" || activeShimmer === "explore") && (
                <motion.span
                  className={`ml-1 ${
                    activeShimmer === "reflect"
                      ? "text-indigo-500"
                      : "text-green-500"
                  }`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}>
                  ✨
                </motion.span>
              )}
            </motion.div>
            <div className="flex flex-wrap gap-2">
              {!isReflectAndPray && data.question !== "Reflect and Pray" && (
                <motion.button
                  onClick={() => handleReflectAndPrayWithLoading()}
                  disabled={loadingSourceId === "reflect-and-pray"}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-all duration-300 cursor-pointer nodrag ${
                    loadingSourceId === "reflect-and-pray"
                      ? "bg-indigo-100 text-indigo-500 border-indigo-300 cursor-wait"
                      : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 hover:scale-105"
                  }`}
                  title={
                    loadingSourceId === "reflect-and-pray"
                      ? "Loading..."
                      : "Reflect and Pray about this topic"
                  }
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    scale:
                      loadingSourceId === "reflect-and-pray"
                        ? [1, 1.02, 1]
                        : activeShimmer === "reflect"
                        ? 0.98
                        : 1,
                    opacity:
                      activeShimmer === "reflect" &&
                      loadingSourceId !== "reflect-and-pray"
                        ? 0.6
                        : 1,
                  }}
                  transition={{
                    scale: {
                      duration: 0.6,
                      repeat:
                        loadingSourceId === "reflect-and-pray" ? Infinity : 0,
                    },
                    opacity: { duration: 0.3 },
                  }}>
                  {loadingSourceId === "reflect-and-pray" ? (
                    <>
                      <Loader2 size={12} className="animate-spin" />
                      <span>🙏 Reflect and Pray</span>
                    </>
                  ) : (
                    <>🙏 Reflect and Pray</>
                  )}
                </motion.button>
              )}

              <motion.button
                onClick={() => handleExploreWithLoading()}
                disabled={loadingSourceId === "explore"}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-all duration-300 cursor-pointer nodrag ${
                  loadingSourceId === "explore"
                    ? "bg-green-100 text-green-500 border-green-300 cursor-wait"
                    : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:border-green-300 hover:scale-105"
                }`}
                title={
                  loadingSourceId === "explore"
                    ? "Loading..."
                    : "I'm feeling blessed..."
                }
                whileTap={{ scale: 0.95 }}
                animate={{
                  scale:
                    loadingSourceId === "explore"
                      ? [1, 1.02, 1]
                      : activeShimmer === "explore"
                      ? 0.98
                      : 1,
                  opacity:
                    activeShimmer === "explore" && loadingSourceId !== "explore"
                      ? 0.6
                      : 1,
                }}
                transition={{
                  scale: {
                    duration: 0.6,
                    repeat: loadingSourceId === "explore" ? Infinity : 0,
                  },
                  opacity: { duration: 0.3 },
                }}>
                {loadingSourceId === "explore" ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    <span>😇 I'm feeling blessed</span>
                  </>
                ) : (
                  <>😇 I'm feeling blessed</>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}

      {/* Continue Journey Section - Only for reflection responses */}
      {isExpanded && isReflection && (
        <motion.div
          className="border-t pt-4 mt-4 mb-3 border-indigo-100 bg-gradient-to-br from-indigo-50/30 via-purple-50/20 to-pink-50/10 rounded-lg"
          initial={{ opacity: 0, height: 0, paddingTop: 0, marginTop: 0 }}
          animate={{
            opacity: 1,
            height: "auto",
            paddingTop: 16,
            marginTop: 16,
          }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            opacity: { delay: 0.2 },
            height: { duration: 0.6 },
          }}>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1.0, // Shorter delay after container appears
              duration: 0.8,
              ease: "easeOut",
            }}>
            <motion.div
              className="text-center mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}>
              <motion.div
                className="text-sm font-medium text-indigo-700 flex items-center justify-center gap-2"
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}>
                <span className="text-lg">🌟</span>
                <span>Continue your journey</span>
              </motion.div>
            </motion.div>

            <div className="flex justify-center items-center">
              <motion.button
                onClick={async () => {
                  if (handleCustomQuestion && !isContinueJourneyLoading) {
                    // Check authentication before allowing Continue Journey
                    if (!isAuthenticated) {
                      setShowLoginModal(true);
                      return;
                    }
                    // IMMEDIATELY start loading state and animation
                    setIsContinueJourneyLoading(true);
                    const element = contentRef.current;

                    if (element) {
                      // Continue Journey flows downward (top-to-bottom shimmer)
                      const shimmerClass = getShimmerDirection("top-to-bottom");

                      element.classList.add(
                        "energy-flow-container",
                        "energy-flow-active",
                        shimmerClass
                      );

                      // Schedule animation cleanup IMMEDIATELY - independent of API
                      setTimeout(() => {
                        element.classList.add("energy-flow-completing");
                      }, energyFlowConfig.continueJourney.duration - 500);

                      setTimeout(() => {
                        element.classList.remove(
                          "energy-flow-container",
                          "energy-flow-active",
                          "energy-flow-completing",
                          shimmerClass
                        );
                      }, energyFlowConfig.continueJourney.duration);
                    }

                    // API call runs completely independently
                    try {
                      // Use handleCustomQuestion for both V1 and V2 systems
                      // It already handles routing to the appropriate API based on systemStyle
                      await handleCustomQuestion(
                        "Continue journey from reflection",
                        data
                      );
                    } catch (error) {
                      console.error("Continue journey failed:", error);
                    } finally {
                      // Only clear loading state when API completes
                      setIsContinueJourneyLoading(false);
                    }
                  }
                }}
                disabled={isContinueJourneyLoading}
                className={`px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 shadow-lg nodrag flex items-center gap-2 ${
                  isContinueJourneyLoading
                    ? "bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-500 text-white/80 cursor-wait"
                    : "bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 text-white hover:from-indigo-600 hover:via-purple-600 hover:to-indigo-700 hover:scale-105 active:scale-95 shadow-indigo-200/50"
                }`}
                whileHover={
                  !isContinueJourneyLoading
                    ? {
                        boxShadow: "0 8px 25px rgba(99, 102, 241, 0.3)",
                      }
                    : {}
                }
                whileTap={!isContinueJourneyLoading ? { scale: 0.95 } : {}}
                animate={{
                  scale: isContinueJourneyLoading ? [1, 1.02, 1] : 1,
                }}
                transition={{
                  scale: {
                    duration: 0.6,
                    repeat: isContinueJourneyLoading ? Infinity : 0,
                  },
                }}>
                {isContinueJourneyLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Finding related themes...</span>
                  </>
                ) : (
                  <span className="flex items-center gap-2">
                    <span>📖</span>
                    <span>Discover Connected Topics</span>
                  </span>
                )}
              </motion.button>
            </div>

            <motion.p
              className="text-center text-xs text-indigo-600/70 mt-3 mb-4 italic"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}>
              Discover related topics to deepen your understanding
            </motion.p>
          </motion.div>
        </motion.div>
      )}

      {/* Ask Input Section */}
      <motion.div
        className="border-t border-gray-200 pt-3 mt-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}>
        <form onSubmit={handleQuestionSubmit} className="nodrag">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isContinueJourney
                  ? "What speaks to your heart?"
                  : "What speaks to your heart?"
              }
              disabled={isLoadingAnswer}
              className={`flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-all ${
                isLoadingAnswer ? "bg-gray-100 cursor-not-allowed" : "bg-white"
              }`}
            />
            <button
              type="submit"
              disabled={!customQuestion.trim() || isLoadingAnswer}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                !customQuestion.trim() || isLoadingAnswer
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 active:scale-95"
              }`}>
              {isLoadingAnswer ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <MessageSquarePlus size={16} />
                  <span>Ask</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>

      {/* Powered by BibeCheck */}
      <motion.div
        className="text-center mt-4 pt-3 border-t border-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}>
        <p className="text-xs text-gray-400 font-medium">
          Powered by <span className="text-blue-600 font-semibold">BibeCheck</span>
        </p>
      </motion.div>

      {/* Energy Flow Status Indicators */}
      {activeShimmer !== "none" && (
        <div className="absolute top-2 right-2 z-10 pointer-events-none">
          <motion.div
            className={`w-3 h-3 rounded-full ${
              activeShimmer === "learnMore"
                ? "bg-purple-600"
                : activeShimmer === "ask"
                ? "bg-blue-600"
                : activeShimmer === "reflect"
                ? "bg-indigo-600"
                : activeShimmer === "explore"
                ? "bg-green-600"
                : activeShimmer === "continueJourney"
                ? "bg-purple-600"
                : "bg-yellow-600"
            }`}
            style={{
              backgroundColor: activeShimmer === "ask" ? "#2D5FEA" : undefined,
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={async (provider) => {
          try {
            const result = await login(provider);
            if (result.success) {
              setShowLoginModal(false);
            }
          } catch (error) {
            console.error("Login failed:", error);
          }
        }}
      />
    </motion.div>
  );
};

export default ResponseNode;
